import { Component, Input, OnInit, inject, OnDestroy } from '@angular/core';
import { IonToolbar, IonButtons, IonButton, IonImg, IonIcon, NavController } from '@ionic/angular/standalone';
import { Router } from '@angular/router';
import { addIcons } from 'ionicons';
import { arrowBackOutline, chatbubbleOutline, notificationsOutline } from 'ionicons/icons';
import { EMPTY, Subscription, catchError, switchMap, timer } from 'rxjs';
import { ThemeToggleComponent } from '../theme-toggle/theme-toggle.component';
import { UserService } from '../../services/user.service';
import { HeaderService } from '../../services/header.service';
import { NotificationService } from '../../services/notification.service';

const NOTIFICATION_POLL_MS = 10_000;

@Component({
  selector: 'app-header',
  template: `
    <ion-toolbar>
      <ion-buttons slot="start" class="header-start">
        @if (showBackButton) {
          <ion-button fill="clear" class="header-back-button clickable" aria-label="Back" (click)="goBack()">
            <ion-icon slot="icon-only" name="arrow-back-outline"></ion-icon>
          </ion-button>
        }
        <ion-img src="assets/Barter_Bridge_logo_registered.svg" alt="Barter Bridge" class="logo clickable" (click)="goToHome()"></ion-img>
      </ion-buttons>
      <ion-buttons slot="end">
        <app-theme-toggle class="clickable"></app-theme-toggle>
        @if (showNotifications) {
          <button type="button" class="icon-container header-icon-button clickable" aria-label="Chats" (click)="goToChats()">
            <ion-icon name="chatbubble-outline" class="chat-icon"></ion-icon>
            @if (unreadChatTotal > 0) {
              <span class="icon-badge">{{ unreadChatTotal > 99 ? '99+' : unreadChatTotal }}</span>
            }
          </button>
          <button type="button" class="icon-container header-icon-button clickable" aria-label="Notifications" (click)="goToNotifications()">
            <ion-icon name="notifications-outline" class="notification-icon"></ion-icon>
            @if (unreadTotal > 0) {
              <span class="icon-badge">{{ unreadTotal > 99 ? '99+' : unreadTotal }}</span>
            }
          </button>
        }
        <button type="button" class="avatar-container header-icon-button clickable" aria-label="Account" (click)="goToAccount()">
          <img [src]="avatarUrl || 'assets/images/placeholder-avatar.svg'" alt="User avatar" (error)="onAvatarError()" />
        </button>
        <ng-content select="[slot=end]"></ng-content>
      </ion-buttons>
    </ion-toolbar>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
    ion-toolbar {
      --background: var(--ion-background-color);
      --padding-start: 16px;
      --padding-end: 16px;
      --padding-top: 4px;
      --padding-bottom: 4px;
    }
    .clickable {
      cursor: pointer;
      pointer-events: auto !important;
    }
    .logo {
      width: 141px;
      height: auto;
    }
    @media (max-width: 767px) {
      .header-start {
        width: 141px;
        min-width: 0;
      }
      .header-start .logo {
        flex: 1 1 auto;
        min-width: 0;
      }
      .header-start .header-back-button {
        flex: 0 0 auto;
        width: 48px;
        min-width: 48px;
        height: 48px;
        margin: 0;
        --padding-start: 8px;
        --padding-end: 8px;
        --color: var(--ion-text-color);
      }
    }
    @media (min-width: 768px) {
      .logo {
        display: none;
      }
    }
    .avatar-container {
      box-sizing: border-box;
      width: 44px;
      height: 44px;
      padding: 4px;
    }
    .avatar-container img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border: 2px solid var(--ion-color-light);
      border-radius: 50%;
    }
    .header-icon-button {
      border: 0;
      background: transparent;
      color: inherit;
      padding: 0;
    }
    .header-icon-button.avatar-container {
      padding: 4px;
    }
    .notification-icon, .chat-icon {
      font-size: 24px;
      color: var(--ion-text-color);
    }
    .icon-container {
      min-width: 44px;
      min-height: 44px;
      justify-content: center;
    }
    ion-buttons {
      gap: 8px;
    }
  `],
  standalone: true,
  imports: [IonToolbar, IonButtons, IonButton, IonImg, IonIcon, ThemeToggleComponent],
})
export class AppHeaderComponent implements OnInit, OnDestroy {
  @Input() showBackButton = false;
  @Input() showNotifications = true;
  @Input() defaultHref = '/home';

  avatarUrl: string | null = null;
  unreadTotal = 0;
  unreadChatTotal = 0;
  private userService = inject(UserService);
  private headerService = inject(HeaderService);
  private router = inject(Router);
  private navController = inject(NavController);
  private notificationService = inject(NotificationService);

  private subscriptions = new Subscription();
  private pollingSubscription?: Subscription;
  private readonly visibilityListener = () => this.updatePollingForVisibility();

  constructor() {
    addIcons({ arrowBackOutline, chatbubbleOutline, notificationsOutline });
  }

  ngOnInit() {
    this.subscriptions.add(
      this.userService.user$.subscribe(user => {
        if (user) {
          this.avatarUrl = user.profileImage || user.avatar || null;
        }
      }),
    );

    this.subscriptions.add(
      this.headerService.showBackButton$.subscribe(show => {
        this.showBackButton = show;
      }),
    );

    this.subscriptions.add(
      this.headerService.defaultHref$.subscribe(href => {
        this.defaultHref = href;
      }),
    );

    this.subscriptions.add(
      this.notificationService.unreadTotal$.subscribe(unreadTotal => {
        this.unreadTotal = unreadTotal;
      }),
    );

    this.subscriptions.add(
      this.notificationService.unreadChatTotal$.subscribe(unreadChatTotal => {
        this.unreadChatTotal = unreadChatTotal;
      }),
    );

    document.addEventListener('visibilitychange', this.visibilityListener);
    this.updatePollingForVisibility();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.pollingSubscription?.unsubscribe();
    document.removeEventListener('visibilitychange', this.visibilityListener);
  }

  goToHome() {
    this.router.navigate(['/home']);
  }

  goBack() {
    if (window.history.state?.navigationId > 1) {
      this.navController.back();
      return;
    }

    this.navController.navigateBack(this.defaultHref);
  }

  goToAccount() {
    this.router.navigate(['/account']);
  }

  goToNotifications() {
    this.router.navigate(['/account/notifications']);
  }

  goToChats() {
    this.router.navigate(['/chats']);
  }

  onAvatarError() {
    this.avatarUrl = 'assets/images/placeholder-avatar.svg';
  }

  private updatePollingForVisibility(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = undefined;

    if (document.visibilityState !== 'visible' || !this.showNotifications) {
      return;
    }

    this.pollingSubscription = timer(0, NOTIFICATION_POLL_MS).pipe(
      switchMap(() => this.notificationService.refreshUnreadTotal().pipe(
        catchError(() => EMPTY),
      )),
    ).subscribe();
  }
}
